import React, { useState, useMemo } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { ctagFilters } from "../filterConfigurations";

const Ctag = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Ctag"]?.edit;
  const {
    data: ctags,
    loading,
    error,
  } = useFirestore("TpCtagCatalog", "CTAN", "asc");
  const [selectedCtag, setSelectedCtag] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "CTAG/ITAG Catalog";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(ctags || [], ctagFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(
    () => [
      { field: "CTAN", headerName: "CTAN", flex: 1, minWidth: 100 },
      { field: "CTAN_Name", headerName: "CTAN Name", flex: 2, minWidth: 200 },
      { field: "EMIS_Code", headerName: "EMIS Code", flex: 1, minWidth: 120 },
      {
        field: "ODE_career_field",
        headerName: "ODE Career Field",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Articulation_Number",
        headerName: "Articulation Number",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Sinclair_Course",
        headerName: "Sinclair Course",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Sinclair_Subject_Short",
        headerName: "Sinclair Subject Short",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Sinclair_Subject_Long",
        headerName: "Sinclair Subject Long",
        flex: 2,
        minWidth: 200,
      },
      { field: "Credits", headerName: "Credits", flex: 1, minWidth: 100 },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((ctag, index) => ({
      id: ctag.id || index, // Use ctag.id if available, otherwise use index
      ...ctag,
    }));
  }, [filteredData]);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedCtag(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCtag(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  const exportColumnOrder = columns.map((col) => col.field);

  const templateFields = Array.from(
    new Set([
      ...exportColumnOrder,
      "Sinclair_Course_and_Subject",
      "ODHE_Portal_In",
      "ODHE_Portal_Note_01",
      "Colleague_TCEQ_In",
      "Colleague_CTAN",
      "Colleague_Start_Date",
      "Colleague_End_Date",
      "Colleague_Note_01",
      "TP_Office_Note_01",
      "OK_to_Post",
      "Status_A",
    ]),
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>Error fetching CTAGs: {error.message}</p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={ctagFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="CTAG/ITAG Catalog Filters"
          resultCount={filteredData?.length || 0}
          totalCount={ctags?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="TpCtagCatalog"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={exportColumnOrder}
              exportData={ctags}
              templateFields={templateFields}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="TpCtagCatalog" />
          )}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            slots={{
              toolbar: () => (
                <SimpleToolbar
                  exportColumnOrder={exportColumnOrder}
                  rows={rows}
                  onBulkActionComplete={handleBulkActionComplete}
                  pageName={pageName}
                />
              ),
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedCtag && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          ctag={selectedCtag}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Ctag;
